/**
 * Pace / HR / power estimates for predicted race rows (must match server/utils/racePredictionMetrics.js).
 */

const PACE_HR_ZACH_WEIGHT = 0.35;
const DRIFT_REF_MAX_HR = 175;

const LTHR_RACE_MULT_KNOTS = [
  [3, 1.075],
  [8, 1.055],
  [20, 1.03],
  [40, 1.012],
  [60, 1.0],
  [90, 0.994],
  [120, 0.988],
  [180, 0.982],
  [240, 0.976],
  [300, 0.971],
];

export function lthrRaceMultiplierForDurationMin(timeMin) {
  if (!Number.isFinite(timeMin) || timeMin < 0) return 1;
  const pts = LTHR_RACE_MULT_KNOTS;
  if (timeMin <= pts[0][0]) return pts[0][1];
  const last = pts[pts.length - 1];
  if (timeMin >= last[0]) return last[1];
  for (let i = 0; i < pts.length - 1; i += 1) {
    const [t0, m0] = pts[i];
    const [t1, m1] = pts[i + 1];
    if (timeMin >= t0 && timeMin <= t1) {
      const w = (timeMin - t0) / (t1 - t0);
      return m0 + w * (m1 - m0);
    }
  }
  return 1;
}

function interpolateRaceHrAnchor(anchors, timeMin, field) {
  const pts = (anchors || [])
    .filter((a) => a[field] != null && Number.isFinite(a[field]) && Number.isFinite(a.durationMin))
    .map((a) => [a.durationMin, a[field]])
    .sort((a, b) => a[0] - b[0]);
  if (pts.length === 0) return null;
  if (pts.length === 1) return pts[0][1];
  if (timeMin <= pts[0][0]) return pts[0][1];
  const last = pts[pts.length - 1];
  if (timeMin >= last[0]) return last[1];
  for (let i = 0; i < pts.length - 1; i += 1) {
    const [t0, v0] = pts[i];
    const [t1, v1] = pts[i + 1];
    if (timeMin >= t0 && timeMin <= t1) {
      const w = (timeMin - t0) / (t1 - t0);
      return v0 + w * (v1 - v0);
    }
  }
  return null;
}

export function blendedLthrRaceMultiplier(timeMin, raceHrProfile) {
  const popMult = lthrRaceMultiplierForDurationMin(timeMin);
  const personalMult = interpolateRaceHrAnchor(raceHrProfile?.anchors, timeMin, 'lthrRatio');
  if (personalMult == null || !Number.isFinite(personalMult)) return popMult;
  const n = raceHrProfile?.sampleCount ?? raceHrProfile?.anchors?.length ?? 0;
  const w = Math.min(1, Math.max(0.35, n / 2));
  return (1 - w) * popMult + w * personalMult;
}

function personalCardiacDriftBonusBpm(timeMin, baseBpm, raceHrProfile) {
  const driftPct = interpolateRaceHrAnchor(raceHrProfile?.anchors, timeMin, 'driftPct');
  if (driftPct == null || !Number.isFinite(driftPct) || driftPct <= 0 || baseBpm <= 0) return 0;
  return Math.round(baseBpm * (driftPct / 200));
}

function cardiacDriftBonusBpm(timeMin, maxHr) {
  if (!Number.isFinite(timeMin) || timeMin < 58) return 0;
  const scale =
    maxHr != null && Number.isFinite(maxHr) && maxHr >= 120 && maxHr <= 230
      ? maxHr / DRIFT_REF_MAX_HR
      : 1;
  let unit = 0;
  if (timeMin <= 118) {
    unit = Math.min(5.5, (timeMin - 58) * 0.085);
  } else {
    const base = Math.min(5.5, (118 - 58) * 0.085);
    unit = Math.min(12, base + (timeMin - 118) * 0.052);
  }
  return unit * scale;
}

function danielsGilbertPctVO2max(timeMin) {
  const t = timeMin;
  return (
    0.8 +
    0.1894393 * Math.exp(-0.012778 * t) +
    0.2989558 * Math.exp(-0.1932605 * t)
  );
}

function pctVO2maxFromHrmaxPctRunning(pctHrMax) {
  const zach = 0.738 * pctHrMax + 26.67;
  const legacy = (pctHrMax - 37.182) / 0.6463;
  const blended = PACE_HR_ZACH_WEIGHT * zach + (1 - PACE_HR_ZACH_WEIGHT) * legacy;
  if (blended < 48 || blended > 96) return null;
  return blended;
}

function pctHrMaxFromPctVO2Percent(pctVO2) {
  if (!Number.isFinite(pctVO2) || pctVO2 < 50 || pctVO2 > 100) return null;
  let lo = 50;
  let hi = 99;
  for (let i = 0; i < 40; i += 1) {
    const mid = (lo + hi) / 2;
    const v = pctVO2maxFromHrmaxPctRunning(mid);
    if (v == null) {
      hi = mid;
      continue;
    }
    if (v < pctVO2) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return (lo + hi) / 2;
}

/**
 * @param {number|null|undefined} timeSeconds
 * @param {number} distanceMeters
 * @param {{ maxHr?: number|null, thresholdBpm?: number|null, thresholdWatts?: number|null, cpWatts?: number|null, raceHrProfile?: { anchors?: object[], sampleCount?: number }|null }} physiology
 */
export function enrichRacePredictionMetrics(timeSeconds, distanceMeters, physiology) {
  const empty = { paceSecPerKm: null, estimatedBpm: null, estimatedWatts: null };
  if (timeSeconds == null || !Number.isFinite(timeSeconds) || distanceMeters <= 0) {
    return empty;
  }
  const km = distanceMeters / 1000;
  if (km <= 0) return empty;

  const paceSecPerKm = Math.round((timeSeconds / km) * 10) / 10;

  const timeMin = timeSeconds / 60;
  const pctRace = danielsGilbertPctVO2max(timeMin);
  const pctRef = danielsGilbertPctVO2max(60);
  if (!Number.isFinite(pctRace) || !Number.isFinite(pctRef) || pctRef <= 0) {
    return { paceSecPerKm, estimatedBpm: null, estimatedWatts: null };
  }
  const ratio = pctRace / pctRef;

  let estimatedBpm = null;
  const thBpm = physiology?.thresholdBpm != null ? Number(physiology.thresholdBpm) : null;
  const maxHr = physiology?.maxHr != null ? Number(physiology.maxHr) : null;
  const hasLthr =
    thBpm != null && Number.isFinite(thBpm) && thBpm >= 80 && thBpm <= 220;

  const raceHrProfile = physiology?.raceHrProfile ?? null;
  const hasPersonalRaceHr =
    raceHrProfile?.sampleCount > 0 &&
    Array.isArray(raceHrProfile.anchors) &&
    raceHrProfile.anchors.length > 0;

  if (hasLthr) {
    const mult = blendedLthrRaceMultiplier(timeMin, raceHrProfile);
    let bpm = thBpm * mult;
    if (!hasPersonalRaceHr) {
      bpm += personalCardiacDriftBonusBpm(timeMin, bpm, raceHrProfile);
    }
    const lo = thBpm * 0.62;
    let hi = thBpm * (hasPersonalRaceHr ? 1.12 : 1.08);
    if (maxHr != null && Number.isFinite(maxHr) && maxHr >= 120 && maxHr <= 230) {
      hi = Math.min(hi, maxHr);
      bpm = Math.min(bpm, maxHr);
    }
    estimatedBpm = Math.round(Math.min(hi, Math.max(lo, bpm)));
  } else if (maxHr != null && Number.isFinite(maxHr) && maxHr >= 120 && maxHr <= 230) {
    const pctVO2percent = pctRace * 100;
    const hrPct = pctHrMaxFromPctVO2Percent(pctVO2percent);
    if (hrPct != null) {
      estimatedBpm = Math.round(
        Math.min(maxHr * 0.985, Math.max(maxHr * 0.52, maxHr * (hrPct / 100)))
      );
    }
  }

  if (estimatedBpm != null && !hasLthr) {
    estimatedBpm = Math.round(estimatedBpm + cardiacDriftBonusBpm(timeMin, maxHr));
  }

  if (estimatedBpm != null) {
    if (maxHr != null && Number.isFinite(maxHr) && maxHr >= 120 && maxHr <= 230) {
      estimatedBpm = Math.min(estimatedBpm, Math.round(maxHr));
    } else if (hasLthr) {
      estimatedBpm = Math.min(estimatedBpm, Math.round(thBpm * 1.12));
    }
  }

  let estimatedWatts = null;
  const cpW = physiology?.cpWatts != null ? Number(physiology.cpWatts) : null;
  const thW = physiology?.thresholdWatts != null ? Number(physiology.thresholdWatts) : null;
  const refW =
    cpW != null && Number.isFinite(cpW) && cpW > 40
      ? cpW
      : thW != null && Number.isFinite(thW) && thW > 40
        ? thW
        : null;
  if (refW != null) {
    estimatedWatts = Math.round(Math.min(refW * 1.22, Math.max(refW * 0.68, refW * ratio)));
  }

  return { paceSecPerKm, estimatedBpm, estimatedWatts };
}
